import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { AssetRelationship, AssetRelationshipFormData, RelationshipType, ImportanceLevel } from '@/types/asset-relationship';

// Função utilitária para buscar o nome do tipo de ativo
const getAssetTypeName = async (tipoId: string): Promise<string> => {
  if (!tipoId) return 'Tipo não definido';
  
  const { data, error } = await supabase
    .from('cmdb_tipos_ativos')
    .select('nome')
    .eq('id', tipoId)
    .single();
    
  if (error || !data) {
    console.error('Erro ao buscar tipo de ativo:', error);
    return 'Tipo não definido';
  }
  
  return data.nome;
};

export const useAssetRelationships = (assetId?: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Buscar dados do usuário incluindo client_id
  const { data: userData } = useQuery({
    queryKey: ['user-data', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('users')
        .select('id, client_id')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Erro ao buscar dados do usuário:', error);
        return null;
      }
      return data;
    },
    enabled: !!user?.id
  });

  // Buscar relacionamentos de um ativo específico
  const {
    data: relationships = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['asset-relationships', assetId],
    queryFn: async () => {
      if (!assetId || !userData?.client_id) return [];

      const { data, error } = await supabase
        .from('asset_relationships')
        .select(`
          *,
          source_asset:cmdb_ativos!source_asset_id(id, nome, tipo_nome, status_operacional),
          target_asset:cmdb_ativos!target_asset_id(id, nome, tipo_nome, status_operacional)
        `)
        .or(`source_asset_id.eq.${assetId},target_asset_id.eq.${assetId}`)
        .eq('client_id', userData.client_id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar relacionamentos:', error);
        throw error;
      }

      return data as AssetRelationship[];
    },
    enabled: !!assetId && !!userData?.client_id,
  });

  // Buscar todos os ativos do cliente para seleção
  const {
    data: availableAssets = [],
    isLoading: isLoadingAssets,
  } = useQuery({
    queryKey: ['available-assets', userData?.client_id, assetId],
    queryFn: async () => {
      if (!userData?.client_id || !assetId) {
        return [];
      }

      const { data, error } = await supabase
        .from('cmdb_ativos')
        .select('id, nome, tipo_nome, status_operacional')
        .eq('client_id', userData.client_id)
        .neq('id', assetId) // Excluir o próprio ativo
        .order('nome');

      if (error) {
        console.error('Erro ao buscar ativos disponíveis:', error);
        throw error;
      }

      return data;
    },
    enabled: !!userData?.client_id,
  });

  // Criar relacionamento
  const createRelationship = useMutation({
    mutationFn: async (data: AssetRelationshipFormData) => {
      if (!assetId || !userData?.client_id || !user?.id) {
        throw new Error('Dados necessários não encontrados');
      }

      const relationshipData = {
        source_asset_id: assetId,
        target_asset_id: data.target_asset_id,
        relationship_type: data.relationship_type,
        description: data.description,
        importance_level: data.importance_level,
        client_id: userData.client_id,
        created_by: user.id,
      };

      const { data: result, error } = await supabase
        .from('asset_relationships')
        .insert([relationshipData])
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar relacionamento:', error);
        throw error;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-relationships', assetId] });
      toast({
        title: 'Relacionamento criado',
        description: 'O relacionamento foi criado com sucesso.',
      });
    },
    onError: (error) => {
      console.error('Erro ao criar relacionamento:', error);
      toast({
        title: 'Erro ao criar relacionamento',
        description: 'Ocorreu um erro ao criar o relacionamento. Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // Deletar relacionamento
  const deleteRelationship = useMutation({
    mutationFn: async (relationshipId: string) => {
      const { error } = await supabase
        .from('asset_relationships')
        .delete()
        .eq('id', relationshipId)
        .eq('client_id', userData?.client_id);

      if (error) {
        console.error('Erro ao deletar relacionamento:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-relationships', assetId] });
      toast({
        title: 'Relacionamento removido',
        description: 'O relacionamento foi removido com sucesso.',
      });
    },
    onError: (error) => {
      console.error('Erro ao deletar relacionamento:', error);
      toast({
        title: 'Erro ao remover relacionamento',
        description: 'Ocorreu um erro ao remover o relacionamento. Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // Buscar dados para o grafo de relacionamentos
  const {
    data: graphData,
    isLoading: isLoadingGraph,
  } = useQuery({
    queryKey: ['asset-graph', assetId],
    queryFn: async () => {
      if (!assetId || !userData?.client_id) return { nodes: [], edges: [] };

      // Buscar o ativo principal
      const { data: mainAsset, error: mainAssetError } = await supabase
        .from('cmdb_ativos')
        .select('id, nome, tipo_nome, tipo_id, status_operacional')
        .eq('id', assetId)
        .single();

      if (mainAssetError) {
        console.error('Erro ao buscar ativo principal:', mainAssetError);
        throw mainAssetError;
      }

      // Buscar todos os relacionamentos conectados (diretos e indiretos)
      const { data: allRelationships, error: relationshipsError } = await supabase
        .from('asset_relationships')
        .select(`
          *,
          source_asset:cmdb_ativos!source_asset_id(id, nome, tipo_nome, tipo_id, status_operacional),
          target_asset:cmdb_ativos!target_asset_id(id, nome, tipo_nome, tipo_id, status_operacional)
        `)
        .or(`source_asset_id.eq.${assetId},target_asset_id.eq.${assetId}`)
        .eq('client_id', userData.client_id);

      if (relationshipsError) {
        console.error('Erro ao buscar relacionamentos para grafo:', relationshipsError);
        throw relationshipsError;
      }

      // Coletar todos os tipo_ids únicos para buscar ícones
      const tipoIds = new Set<string>();
      if (mainAsset.tipo_id) tipoIds.add(mainAsset.tipo_id);
      (allRelationships || []).forEach((rel) => {
        if (rel.source_asset?.tipo_id) tipoIds.add(rel.source_asset.tipo_id);
        if (rel.target_asset?.tipo_id) tipoIds.add(rel.target_asset.tipo_id);
      });

      // Buscar tipos de ativos com ícones
      let tipoMap = new Map<string, { nome: string; icone: string | null }>();
      if (tipoIds.size > 0) {
        const { data: tipos } = await supabase
          .from('cmdb_tipos_ativos')
          .select('id, nome, icone')
          .in('id', Array.from(tipoIds));
        tipos?.forEach((t) => tipoMap.set(t.id, { nome: t.nome, icone: t.icone }));
      }

      // Construir nós únicos
      const nodeMap = new Map();
      
      // Adicionar o ativo principal
      const getTypeName = (asset: any) => {
        if (asset.tipo_id && tipoMap.has(asset.tipo_id)) return tipoMap.get(asset.tipo_id)!.nome;
        if (asset.tipo_nome && !/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(asset.tipo_nome)) return asset.tipo_nome;
        return 'Ativo';
      };

      const getIcon = (asset: any): string | undefined => {
        if (asset.tipo_id && tipoMap.has(asset.tipo_id)) return tipoMap.get(asset.tipo_id)!.icone || undefined;
        return undefined;
      };

      nodeMap.set(mainAsset.id, {
        id: mainAsset.id,
        name: mainAsset.nome,
        type: getTypeName(mainAsset),
        icon: getIcon(mainAsset),
        status: mainAsset.status_operacional || 'unknown',
        importance: 'high' as const,
        isMain: true,
      });

      // Adicionar ativos relacionados
      allRelationships.forEach((rel) => {
        if (rel.source_asset && !nodeMap.has(rel.source_asset.id)) {
          nodeMap.set(rel.source_asset.id, {
            id: rel.source_asset.id,
            name: rel.source_asset.nome,
            type: getTypeName(rel.source_asset),
            icon: getIcon(rel.source_asset),
            status: rel.source_asset.status_operacional || 'unknown',
            importance: rel.importance_level as ImportanceLevel,
          });
        }
        
        if (rel.target_asset && !nodeMap.has(rel.target_asset.id)) {
          nodeMap.set(rel.target_asset.id, {
            id: rel.target_asset.id,
            name: rel.target_asset.nome,
            type: getTypeName(rel.target_asset),
            icon: getIcon(rel.target_asset),
            status: rel.target_asset.status_operacional || 'unknown',
            importance: rel.importance_level as ImportanceLevel,
          });
        }
      });

      // Construir arestas
      const edges = allRelationships.map((rel) => ({
        id: rel.id,
        source: rel.source_asset_id,
        target: rel.target_asset_id,
        type: rel.relationship_type as RelationshipType,
        importance: rel.importance_level as ImportanceLevel,
        label: rel.description || '',
      }));

      return {
        nodes: Array.from(nodeMap.values()),
        edges,
      };
    },
    enabled: !!assetId && !!userData?.client_id,
  });

  return {
    relationships,
    isLoading,
    error,
    availableAssets,
    isLoadingAssets,
    createRelationship,
    deleteRelationship,
    graphData,
    isLoadingGraph,
  };
};
